import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';
import LoadingSimulator from 'Pages/CampaignCreation/components/GeneratedCampaign/LoadingSimulator';
import GeneratedCampaignCheckout from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignCheckout';
import CampaignCreationSkeleton from 'Pages/CampaignCreation/components/CampaignCreationSkeleton/CampaignCreationSkeleton';
import {
  BANNER_CPM,
  BANNER_FREQ,
  DEFAULT_MIN_FLIGHT_DATES,
  MIN_IMPRESSIONS_COUNT,
} from 'Utils/constants';
import { adjustDates } from 'Utils/helpers';
import { CampaignStatusEnum, CampaignStep } from 'enums';
import type { AudienceType, GeneratedCampaignType } from 'types';
import { getGeneratedCampaignById } from 'Requests/Request_Methods/campaignMethods';
import './GeneratedCampaign.scss';

export type generatedCampaignCostType = {
  campaign: GeneratedCampaignType | null;
  startDate: Date;
  endDate: Date;
  minFlightDuration: number;
  bannerCPM: number;
  bannerFreq: number;
  suggestedDates: number;
  isDateSelected: boolean;
  isConfirmed: boolean;
  excludedAudiences: number[];
  changedAudiences: AudienceType[];
  clickUrl?: string;
};
const GeneratedCampaign = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const history = useHistory();
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const { startDate: initialStartDate, endDate: initialEndDate } = useMemo(() => {
    return adjustDates(moment().toDate());
  }, []);
  const [deviceWidth, setDeviceWidth] = useState(window.innerWidth);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [campaignCostAccData, setCampaignCostAccData] = useState<generatedCampaignCostType>({
    campaign: null,
    startDate: initialStartDate,
    endDate: initialEndDate,
    minFlightDuration: DEFAULT_MIN_FLIGHT_DATES,
    suggestedDates: DEFAULT_MIN_FLIGHT_DATES,
    bannerCPM: BANNER_CPM,
    bannerFreq: BANNER_FREQ,
    excludedAudiences: [],
    changedAudiences: [],
    isDateSelected: false,
    isConfirmed: false,
    clickUrl: '',
  });

  useEffect(() => {
    const handleResize = () => {
      setDeviceWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCampaign = async () => {
    try {
      setLoading(true);
      const { data: campaign } = await getGeneratedCampaignById(campaignId);
      if (campaign) {
        if (campaign.campaign.status === CampaignStatusEnum.ERROR) {
          if (campaign.campaign.step === CampaignStep.AUDIENCE) {
            history.replace(`/create-campaign/${campaign.campaign.id}/setup`);
          }
          if (campaign.campaign.step === CampaignStep.CREATIVE) {
            history.replace(`/create-campaign/${campaign.campaign.id}/creatives`);
          }
          if (campaign.campaign.step === CampaignStep.CHECKOUT) {
            history.replace(`/create-campaign/${campaign.campaign.id}/calculate`);
          }
        }
        if (campaign.campaign.step === CampaignStep.DONE) {
          history.replace('/orderlines');
        }
        setSelectedItems(campaign.gen_banners.map(item => item.banner_uuid));

        const overalMinDays = DEFAULT_MIN_FLIGHT_DATES;
        let { suggestedDates, minFlightDuration } = campaignCostAccData;

        if (suggestedDates <= overalMinDays) {
          suggestedDates = overalMinDays;
        }
        if (suggestedDates >= overalMinDays) {
          minFlightDuration = overalMinDays;
        }

        const { endDate } = adjustDates(moment().toDate(), suggestedDates);
        setCampaignCostAccData(prevData => ({
          ...prevData,
          campaign,
          endDate,
          suggestedDates,
          minFlightDuration,
          clickUrl: campaign.listing.click_url,
        }));

        if (campaign.campaign.is_seen) {
          setShowCheckout(true); // Show immediately
        } else {
          setTimeout(() => {
            setShowCheckout(true);
          }, 6000);
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error('An error occurred while generating your campaign.');
      history.push(deviceWidth <= 600 ? '/dashboard' : '/orderlines?filter=campaigns');
    }
  };

  useEffect(() => {
    getCampaign();
    return () => {
      setCampaignCostAccData(prevData => ({
        ...prevData,
        campaign: null,
      }));
    };
  }, [campaignId]);

  if (loading || (!campaignCostAccData.campaign && !showCheckout)) {
    return <CampaignCreationSkeleton />;
  }

  if (!campaignCostAccData.campaign?.campaign.is_seen && !showCheckout) {
    return <LoadingSimulator />;
  }
  return (
    <GeneratedCampaignCheckout
      campaignCostAccData={campaignCostAccData}
      selectedItems={selectedItems}
      setSelectedItems={setSelectedItems}
      setCampaignCostAccData={setCampaignCostAccData}
      onUpdateCampaign={getCampaign}
    />
  );
};

export default GeneratedCampaign;
